/**
 * REST client for the Phase 2 matchmaker handshake.
 *
 * - POST /v1/match/queue/enter  → returns wsUrl + matchId on success.
 * - POST /v1/match/queue/cancel → boolean ok.
 *
 * Auth follows the same `Authorization: tma <initData>` model as the rest of
 * the app (apps/web/src/api/client.ts). 4xx envelopes for `enter` are
 * returned as the typed `QueueEnterError` so callers can branch on `reason`
 * instead of catching exceptions.
 */
import type {
  MatchHistoryResponse,
  QueueEnterError,
  QueueEnterRequest,
  QueueEnterResponse,
  RoomCancelError,
  RoomCancelResult,
  RoomCancelSuccess,
  RoomCreateError,
  RoomCreateResponse,
  RoomCreateResult,
  RoomJoinError,
  RoomJoinResult,
  RoomJoinSuccess,
} from '@sagwa/types'

const BASE_URL: string =
  (typeof import.meta !== 'undefined' &&
    (import.meta as ImportMeta & { env?: Record<string, string> }).env?.VITE_API_BASE_URL) ||
  ''

function getInitData(): string {
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : undefined
  return tg?.initData ?? ''
}

function authHeaders(): Record<string, string> {
  const initData = getInitData()
  if (!initData) return {}
  return { Authorization: `tma ${initData}` }
}

/**
 * Normalize a 4xx/5xx body from /v1/match/room/{create,join} into a typed
 * error envelope. Unknown error strings collapse to `INTERNAL` so the UI
 * always has a non-null branch.
 */
function parseRoomError(body: unknown, kind: 'create'): RoomCreateError
function parseRoomError(body: unknown, kind: 'join'): RoomJoinError
function parseRoomError(
  body: unknown,
  kind: 'create' | 'join',
): RoomCreateError | RoomJoinError {
  const error =
    body && typeof body === 'object' && 'error' in body
      ? (body as { error?: string }).error
      : undefined
  if (kind === 'create') {
    if (
      error === 'LIVE_MATCH_DISABLED' ||
      error === 'STAMINA_EMPTY' ||
      error === 'RATE_LIMITED' ||
      error === 'INTERNAL'
    ) {
      return { ok: false, error }
    }
    return { ok: false, error: 'INTERNAL' }
  }
  if (
    error === 'LIVE_MATCH_DISABLED' ||
    error === 'ROOM_NOT_FOUND' ||
    error === 'INVALID_CODE' ||
    error === 'NETWORK_ERROR' ||
    error === 'STAMINA_EMPTY' ||
    error === 'RATE_LIMITED' ||
    error === 'INTERNAL'
  ) {
    return { ok: false, error }
  }
  return { ok: false, error: 'INTERNAL' }
}

export const matchClient = {
  async enterQueue(
    opts: QueueEnterRequest,
  ): Promise<QueueEnterResponse | QueueEnterError> {
    let res: Response
    try {
      res = await fetch(`${BASE_URL}/v1/match/queue/enter`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify(opts),
      })
    } catch {
      return { ok: false, reason: 'INTERNAL' }
    }
    const body: unknown = await res.json().catch(() => null)
    if (!res.ok) {
      const reason =
        body && typeof body === 'object' && 'reason' in body
          ? (body as { reason?: QueueEnterError['reason'] }).reason
          : undefined
      if (
        reason === 'NO_STAMINA' ||
        reason === 'ALREADY_QUEUED' ||
        reason === 'IN_MATCH' ||
        reason === 'RATE_LIMITED' ||
        reason === 'INTERNAL'
      ) {
        return { ok: false, reason }
      }
      return { ok: false, reason: 'INTERNAL' }
    }
    return body as QueueEnterResponse
  },

  /**
   * Mint a friend-room code via POST /v1/match/room/create. Server debits 1
   * stamina on success and refunds on every downstream failure path. Caller
   * branches on `result.ok`:
   *   - ok=true  → render RoomCodeDisplay with `result.code`
   *   - ok=false → render i18n error via `result.error` (see
   *     `liveMatch.room.error.*` keys; `LIVE_MATCH_DISABLED` should be
   *     unreachable when the visual gate is correct but defensive guard).
   *
   * W3+W4 P1-b: `targetSize` (2-99 clamp on server) is forwarded to OCI so
   * the room's auto-start threshold matches the owner's choice. Omitted →
   * server default 2 (MIN_MATCH_PLAYERS).
   */
  async createRoom(opts: { targetSize?: number } = {}): Promise<RoomCreateResult> {
    let res: Response
    try {
      const body: Record<string, unknown> = {}
      if (opts.targetSize !== undefined) body.targetSize = opts.targetSize
      res = await fetch(`${BASE_URL}/v1/match/room/create`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify(body),
      })
    } catch {
      const err: RoomCreateError = { ok: false, error: 'INTERNAL' }
      return err
    }
    const body: unknown = await res.json().catch(() => null)
    if (!res.ok) {
      const e = parseRoomError(body, 'create')
      return e
    }
    return body as RoomCreateResponse
  },

  /**
   * W3+W4 P1-d: cancel a friend room (owner-only, lobby-only).
   *
   * Returns RoomCancelSuccess on 200 (includes `refunded` for idempotent
   * second-call detection) or RoomCancelError on 4xx/5xx. Network failures
   * surface as `INTERNAL` so the caller never sees an exception.
   */
  async cancelRoom(matchId: string): Promise<RoomCancelResult> {
    let res: Response
    try {
      res = await fetch(`${BASE_URL}/v1/match/room/cancel`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({ matchId }),
      })
    } catch {
      const err: RoomCancelError = { ok: false, error: 'INTERNAL' }
      return err
    }
    const body: unknown = await res.json().catch(() => null)
    if (!res.ok) {
      const error =
        body && typeof body === 'object' && 'error' in body
          ? (body as { error?: string }).error
          : undefined
      if (
        error === 'LIVE_MATCH_DISABLED' ||
        error === 'ROOM_NOT_LOBBY' ||
        error === 'NOT_OWNER' ||
        error === 'RATE_LIMITED' ||
        error === 'INTERNAL'
      ) {
        return { ok: false, error }
      }
      return { ok: false, error: 'INTERNAL' }
    }
    return body as RoomCancelSuccess
  },

  /**
   * Resolve a friend-room code via POST /v1/match/room/join. The Worker
   * collapses ROOM_NOT_FOUND / ROOM_EXPIRED / RATE_LIMITED into a single
   * `ROOM_NOT_FOUND` error to avoid leaking valid codes to attackers
   * (W2 plan Risk #2).
   */
  async joinRoom(code: string): Promise<RoomJoinResult> {
    let res: Response
    try {
      res = await fetch(`${BASE_URL}/v1/match/room/join`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...authHeaders(),
        },
        body: JSON.stringify({ code }),
      })
    } catch {
      const err: RoomJoinError = { ok: false, error: 'NETWORK_ERROR' }
      return err
    }
    const body: unknown = await res.json().catch(() => null)
    if (!res.ok) {
      const e = parseRoomError(body, 'join')
      return e
    }
    return body as RoomJoinSuccess
  },

  async cancelQueue(): Promise<{ ok: boolean }> {
    try {
      const res = await fetch(`${BASE_URL}/v1/match/queue/cancel`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          ...authHeaders(),
        },
      })
      return { ok: res.ok }
    } catch {
      return { ok: false }
    }
  },

  /**
   * Fetch the read-only leaderboard for a completed match. Shape matches
   * apps/api/src/routes/match.ts /match/:id/leaderboard. Returns null on
   * any error so callers can render an empty state without try/catch.
   */
  async getMatchLeaderboard(
    matchId: string,
    limit = 99,
  ): Promise<MatchLeaderboardResponse | null> {
    try {
      const res = await fetch(
        `${BASE_URL}/v1/match/match/${encodeURIComponent(matchId)}/leaderboard?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...authHeaders(),
          },
        },
      )
      if (!res.ok) return null
      const body = (await res.json()) as MatchLeaderboardResponse
      return body
    } catch {
      return null
    }
  },

  /**
   * Paginated list of the user's past matches. Backed by
   * GET /v1/match/history. Returns null on any error so callers can render
   * an empty state without try/catch.
   */
  async getMatchHistory(
    limit = 20,
    offset = 0,
  ): Promise<MatchHistoryResponse | null> {
    try {
      const res = await fetch(
        `${BASE_URL}/v1/match/history?limit=${limit}&offset=${offset}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            ...authHeaders(),
          },
        },
      )
      if (!res.ok) return null
      const body = (await res.json()) as MatchHistoryResponse
      return body
    } catch {
      return null
    }
  },
}

export type MatchClient = typeof matchClient

/**
 * Shape returned by GET /v1/match/match/:id/leaderboard. Mirrored from
 * apps/api/src/routes/match.ts; no schema lives in @sagwa/types yet
 * because nothing else on the web side needs it.
 */
export type MatchLeaderboardEntry = {
  rank: number
  userId: number
  displayName: string
  score: number
  durationMs: number
  moveCount: number
  submittedAt: string
}

export type MatchLeaderboardResponse = {
  matchId: string
  entries: MatchLeaderboardEntry[]
}
